import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { dinero, toDecimal } from 'dinero.js';

import { VoucherInfo } from '../types/activity-column-types';

export function formatQuantity(item: BackofficeActivitiesOrderItem): number {
  const rawQuantity =
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
      ? Number(item.quantity_free_goods)
      : item.quantity !== undefined
      ? Number(item.quantity)
      : 0;

  const sanitizedQuantity = Number.isFinite(rawQuantity)
    ? Math.trunc(rawQuantity)
    : 0;

  return Number(
    toDecimal(
      dinero({
        amount: sanitizedQuantity,
        currency: STOCK_UI,
      })
    )
  );
}

export function calculateVoucherDiscount(
  price: number,
  voucherInfo: VoucherInfo
): { finalPrice: number; originalPrice: number; discountAmount?: number } {
  const originalPrice = price;
  let finalPrice = price;

  if (!voucherInfo.hasVoucherDiscount) {
    return { finalPrice, originalPrice };
  }

  if (voucherInfo.voucherDiscountPercentage) {
    const voucherDiscount =
      (finalPrice * voucherInfo.voucherDiscountPercentage) / 100;
    finalPrice = finalPrice - voucherDiscount;

    return {
      finalPrice,
      originalPrice,
      discountAmount: voucherInfo.voucherDiscountPercentage * 100,
    };
  }

  if (voucherInfo.voucherDiscountAmount) {
    finalPrice = finalPrice - voucherInfo.voucherDiscountAmount;

    return {
      finalPrice,
      originalPrice,
    };
  }

  return { finalPrice, originalPrice };
}

export function formatCurrency(amount: number): string {
  return buyerCurrencyFormatter(amount);
}

export interface ActivityImageObject {
  image?: string;
  thumbnail_url?: string;
  medium_image_url?: string;
  large_image_url?: string;
}

export function getItemCode(item: BackofficeActivitiesOrderItem): string {
  const convertToString = (
    value: string | number | undefined | null
  ): string => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  };

  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    const freeGoodsCode = convertToString(item.free_goods_product_item_code);
    if (freeGoodsCode) {
      return freeGoodsCode;
    }

    const productCode = convertToString(item.product_item_code);
    if (productCode) {
      return productCode;
    }

    const itemCode =
      'item_code' in item && item.item_code !== undefined
        ? convertToString(item.item_code)
        : '';
    if (itemCode) {
      return itemCode;
    }

    return '';
  }

  const productCode = convertToString(item.product_item_code);
  if (productCode) {
    return productCode;
  }

  const itemCode =
    'item_code' in item && item.item_code !== undefined
      ? convertToString(item.item_code)
      : '';
  if (itemCode) {
    return itemCode;
  }

  return '';
}

export function getImageUrl(item: BackofficeActivitiesOrderItem): string {
  const placeholder = 'assets/images/product_placeholder.png';

  const firstNonEmpty = (arr: unknown): string | undefined => {
    if (!Array.isArray(arr)) return undefined;
    return arr.find((url: string) => url);
  };

  // Handle array-based image fields (order items from API)
  const fromArrays =
    firstNonEmpty(item.images_medium) ||
    firstNonEmpty(item.images) ||
    firstNonEmpty(item.images_large) ||
    firstNonEmpty(item.image_thumbnails);

  if (fromArrays) return fromArrays;

  // Handle object-based image fields (promo/voucher free good items)
  const images = item.images;
  if (images && typeof images === 'object' && !Array.isArray(images)) {
    const { medium_image_url, image, large_image_url, thumbnail_url } =
      images as ActivityImageObject;
    return (
      medium_image_url ||
      image ||
      large_image_url ||
      thumbnail_url ||
      placeholder
    );
  }

  return placeholder;
}

export function isNewProduct(item: BackofficeActivitiesOrderItem): boolean {
  return Boolean(item.is_new_product);
}

export function hasFreeGoods(item: BackofficeActivitiesOrderItem): boolean {
  return Boolean(
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
  );
}

export function formatVoucherDiscount(discount: {
  voucher_discount_percentage?: number;
  voucher_discount_amount?: number;
}): string {
  if (discount.voucher_discount_percentage) {
    return `Disc. ${discount.voucher_discount_percentage}%`;
  }

  if (discount.voucher_discount_amount) {
    return `Disc. Rp ${formatCurrency(discount.voucher_discount_amount)}`;
  }

  return '';
}

export function formatProductRewardImages(
  images: {
    image?: string;
    thumbnail_url?: string;
    medium_image_url?: string;
    large_image_url?: string;
  },
  defaultPlaceholder = 'assets/images/product_placeholder.png'
): {
  image: string;
  thumbnail_url: string;
  medium_image_url: string;
  large_image_url: string;
} {
  return {
    image: images.image || defaultPlaceholder,
    thumbnail_url: images.thumbnail_url || defaultPlaceholder,
    medium_image_url: images.medium_image_url || defaultPlaceholder,
    large_image_url: images.large_image_url || defaultPlaceholder,
  };
}

export function parseVoucherRuleProduct(ruleProduct: {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
}): {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
} {
  return {
    is_rule_product: ruleProduct.is_rule_product,
    voucher_codes: ruleProduct.voucher_codes || [],
    rules: ruleProduct.rules || [],
  };
}
